import { useTranslation } from 'react-i18next';
import { trpc } from '@org/clients/school';
import { CohortHeatmap, StatTile, type ChartSeries } from '@org/ui';
import { useAnalytics } from '../analytics-context';
import { ReportCard } from '../report-card';
import { TimeSeriesCard } from '../time-series-card';
import { tileDelta } from '../compare';

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

export function OverviewReports() {
  const { t } = useTranslation('admin');
  const { input, compareInput, search, rangeDays } = useAnalytics();
  const comparing = Boolean(compareInput);
  const summary = trpc.analytics.overview.summary.useQuery(input);
  const cmpSummary = trpc.analytics.overview.summary.useQuery(compareInput ?? input, {
    enabled: search.compare === 'lastYear',
  });
  const active = trpc.analytics.overview.activeStudents.useQuery(input);
  const cmpActive = trpc.analytics.overview.activeStudents.useQuery(compareInput ?? input, { enabled: comparing });
  const mix = trpc.analytics.overview.activityMix.useQuery(input);
  const cmpMix = trpc.analytics.overview.activityMix.useQuery(compareInput ?? input, { enabled: comparing });
  const heatmap = trpc.analytics.overview.activityHeatmap.useQuery(input);

  const vsLabel =
    search.compare === 'lastYear'
      ? t('analytics.compare.vsLastYear')
      : t('analytics.common.vsPrevious', { days: rangeDays });
  const cur = summary.data?.current;
  const cmp = search.compare === 'lastYear' ? cmpSummary.data?.current : search.compare === 'previous' ? summary.data?.previous : undefined;

  const activeSeries: ChartSeries[] = [{ key: 'active', label: t('analytics.overview.activeStudents.series') }];
  const mixSeries: ChartSeries[] = [
    { key: 'class', label: t('analytics.overview.activityMix.series.class') },
    { key: 'visit', label: t('analytics.overview.activityMix.series.visit') },
    { key: 'submission', label: t('analytics.overview.activityMix.series.submission') },
    { key: 'chat', label: t('analytics.overview.activityMix.series.chat') },
    { key: 'conversation', label: t('analytics.overview.activityMix.series.conversation') },
  ];

  const tiles = [
    {
      key: 'scopeStudents',
      label: t('analytics.overview.tiles.scopeStudents'),
      value: cur?.scopeStudents ?? null,
      hint: t('analytics.overview.tiles.scopeStudentsHint'),
    },
    {
      key: 'activeStudents',
      label: t('analytics.overview.tiles.activeStudents'),
      value: cur?.activeStudents ?? null,
      delta: tileDelta(cur?.activeStudents, cmp?.activeStudents, vsLabel),
      hint:
        cur && cur.scopeStudents > 0
          ? t('analytics.overview.tiles.activeStudentsHint', { pct: Math.round((cur.activeStudents / cur.scopeStudents) * 100) })
          : undefined,
    },
    {
      key: 'newStudents',
      label: t('analytics.overview.tiles.newStudents'),
      value: cur?.newStudents ?? null,
      delta: tileDelta(cur?.newStudents, cmp?.newStudents, vsLabel),
    },
    {
      key: 'classesHeld',
      label: t('analytics.overview.tiles.classesHeld'),
      value: cur?.classesHeld ?? null,
      delta: tileDelta(cur?.classesHeld, cmp?.classesHeld, vsLabel),
    },
    {
      key: 'attendanceRate',
      label: t('analytics.overview.tiles.attendanceRate'),
      value: cur?.attendanceRate ?? null,
      format: 'percent' as const,
      delta: tileDelta(cur?.attendanceRate, cmp?.attendanceRate, vsLabel, { percentPoints: true }),
      hint: t('analytics.overview.tiles.attendanceRateHint'),
    },
    {
      key: 'submissions',
      label: t('analytics.overview.tiles.submissions'),
      value: cur?.submissions ?? null,
      delta: tileDelta(cur?.submissions, cmp?.submissions, vsLabel),
    },
    {
      key: 'chatMessages',
      label: t('analytics.overview.tiles.chatMessages'),
      value: cur?.chatMessages ?? null,
      delta: tileDelta(cur?.chatMessages, cmp?.chatMessages, vsLabel),
    },
  ];

  const hm = heatmap.data;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        {tiles.map((tile) => (
          <StatTile
            key={tile.key}
            label={tile.label}
            value={summary.isLoading ? null : tile.value}
            format={tile.format}
            delta={tile.delta}
            hint={tile.hint}
          />
        ))}
      </div>

      <TimeSeriesCard
        title={t('analytics.overview.activeStudents.title')}
        description={t('analytics.overview.activeStudents.description')}
        data={active.data}
        cmpData={cmpActive.data}
        series={activeSeries}
        isLoading={active.isLoading}
        error={active.error}
      />

      <TimeSeriesCard
        title={t('analytics.overview.activityMix.title')}
        description={t('analytics.overview.activityMix.description')}
        data={mix.data}
        cmpData={cmpMix.data}
        series={mixSeries}
        stacked
        isLoading={mix.isLoading}
        error={mix.error}
      />

      <ReportCard
        title={t('analytics.overview.heatmap.title')}
        description={t('analytics.overview.heatmap.description')}
        isLoading={heatmap.isLoading}
        error={heatmap.error}
        isEmpty={hm?.total === 0}
      >
        <CohortHeatmap
          showSize={false}
          max={hm?.max ?? 1}
          rowLabel={t('analytics.overview.heatmap.weekday')}
          valueFormat={(n) => String(n)}
          columnLabels={Array.from({ length: 24 }, (_, h) => `${h}h`)}
          rows={(hm?.matrix ?? []).map((cells, d) => ({
            key: WEEKDAYS[d],
            label: t(`analytics.overview.heatmap.days.${WEEKDAYS[d]}`),
            size: cells.reduce((a, b) => a + b, 0),
            cells: cells.map((c) => (c === 0 ? null : c)),
          }))}
        />
      </ReportCard>
    </div>
  );
}
